import {
  FlightsAdvancedFilterType,
  FlightsTripType,
  LodgingPwaPropertySearch,
  OperationResult,
  Points,
  TravelerDetailsType,
} from '__generated__/api';
import { getLocKeyFromBexApiDateTime } from 'components/multi-item/bex-api-date-time-to-time-loc-key';

export enum SortAndFilterOptions {
  SORT = 'sort',
  ACCESSIBILITY = 'accessibility',
  AGENCY_BUSINESS_MODELS = 'agencyBusinessModels',
  AMENITIES = 'amenities',
  BEDROOM_FILTER = 'bedroomFilter',
  CHAIN = 'chain',
  CLEANLINESS = 'cleaningAndSafetyPractices',
  COMMISSION_TIERS = 'commissionTiers',
  DEALS = 'deals',
  MEAL_PLANS = 'mealPlan',
  REGION_ID = 'regionId',
  PAYMENT_TYPE = 'paymentType',
  POI = 'poi',
  PRICE = 'price',
  PRICE_BUCKETS = 'priceBuckets',
  PROPERTY_NAME = 'hotelName',
  PROPERTY_STYLES = 'propertyStyles',
  REVIEW_SCORE = 'guestRating',
  REWARDS = 'rewards',
  STAR = 'star',
  STAY_TYPE = 'stayType',
  STRUCTURE_TYPE = 'lodging',
  TRAVELER_TYPE = 'travelerType',
}

export enum SearchIntentFields {
  SEMDTL = 'semdtl',
  THEME = 'theme',
  USER_INTENT = 'userIntent',
}

export enum PropertyShopOptions {
  USE_REWARDS = 'useRewards',
  TYPEAHEAD_REGION_TYPE = 'typeaheadRegionType',
}

/**
 * Javascript Date objects require a month index which is why we need minus 1
 * TODO: Add date string logic to the BEX API SDK
 */
const getDateStringFromDateRange = (dateRange: LodgingPwaPropertySearch.CheckInDate | LodgingPwaPropertySearch.CheckOutDate): string =>
  new Date(Date.UTC(dateRange.year, dateRange.month - 1, dateRange.day)).toISOString().split('T')[0];

export const adaptRequestToDict = (criteria: LodgingPwaPropertySearch.Criteria): any => {
  const requestFieldsDict: any = {};

  const dateRange = criteria.primary.dateRange;
  if (dateRange) {
    requestFieldsDict.startDate = getDateStringFromDateRange(dateRange.checkInDate);
    requestFieldsDict.endDate = getDateStringFromDateRange(dateRange.checkOutDate);
  }

  const destination = criteria.primary.destination;

  if (destination.regionId) {
    requestFieldsDict.regionId = destination.regionId;
  }
  if (destination.regionName) {
    requestFieldsDict.destination = destination.regionName;
  }

  const criteriaCounts = criteria.secondary?.counts;

  if (criteriaCounts) {
    const typeaheadRegionType = getValueFromCounts(PropertyShopOptions.TYPEAHEAD_REGION_TYPE, criteriaCounts);
    if (typeaheadRegionType) {
      requestFieldsDict.typeaheadRegionType = typeaheadRegionType;
    }
  }

  const criteriaSelections = criteria.secondary?.selections;

  if (criteriaSelections) {
    const theme = getValueFromSelections(SearchIntentFields.THEME, criteriaSelections);
    requestFieldsDict.theme = theme ? theme : '';

    const userIntent = getValueFromSelections(SearchIntentFields.USER_INTENT, criteriaSelections);
    requestFieldsDict.userIntent = userIntent ? userIntent : '';

    const semdtl = getValueFromSelections(SearchIntentFields.SEMDTL, criteriaSelections);
    requestFieldsDict.semdtl = semdtl ? semdtl : '';
  }

  return requestFieldsDict;
};

export const adaptRequestSortAndFiltersToDict = (criteria: LodgingPwaPropertySearch.Criteria): any => {
  const requestFieldsDict: any = {};

  const selections = criteria.secondary?.selections;

  if (selections) {
    const points = getValueFromSelections(PropertyShopOptions.USE_REWARDS, selections);
    if (points) {
      requestFieldsDict.useRewards = Points.SHOP_WITHOUT_POINTS === points ? 'false' : 'true';
    }

    const sort = getValueFromSelections(SortAndFilterOptions.SORT, selections);
    if (sort) {
      requestFieldsDict.sort = sort;
    }

    const filtersFromSelections = {
      accessibility: getListFromSelections(SortAndFilterOptions.ACCESSIBILITY, selections),
      agencyBusinessModel: getListFromSelections(SortAndFilterOptions.AGENCY_BUSINESS_MODELS, selections),
      amenities: getListFromSelections(SortAndFilterOptions.AMENITIES, selections),
      bedroomFilter: getListFromSelections(SortAndFilterOptions.BEDROOM_FILTER, selections),
      chain: getValueFromSelections(SortAndFilterOptions.CHAIN, selections),
      cleaningAndSafetyPractices: getListFromSelections(SortAndFilterOptions.CLEANLINESS, selections),
      commissionTier: getListFromSelections(SortAndFilterOptions.COMMISSION_TIERS, selections),
      deals: getListFromSelections(SortAndFilterOptions.DEALS, selections),
      mealPlan: getListFromSelections(SortAndFilterOptions.MEAL_PLANS, selections),
      neighborhood: getValueFromSelections(SortAndFilterOptions.REGION_ID, selections),
      paymentType: getListFromSelections(SortAndFilterOptions.PAYMENT_TYPE, selections),
      poi: getValueFromSelections(SortAndFilterOptions.POI, selections),
      hotelName: getValueFromSelections(SortAndFilterOptions.PROPERTY_NAME, selections),
      propertyStyle: getListFromSelections(SortAndFilterOptions.PROPERTY_STYLES, selections),
      guestRating: getValueFromSelections(SortAndFilterOptions.REVIEW_SCORE, selections),
      rewards: getListFromSelections(SortAndFilterOptions.REWARDS, selections),
      star: getListFromSelections(SortAndFilterOptions.STAR, selections),
      lodging: getListFromSelections(SortAndFilterOptions.STRUCTURE_TYPE, selections),
      travelerType: getListFromSelections(SortAndFilterOptions.TRAVELER_TYPE, selections),
      stayType: getListFromSelections(SortAndFilterOptions.STAY_TYPE, selections),
    };

    Object.keys(filtersFromSelections).forEach(filter => {
      const filterVal = filtersFromSelections[filter];
      if (Array.isArray(filterVal)) {
        if (filterVal.length) {
          requestFieldsDict[filter] = filterVal;
        }
      } else if (filterVal && typeof filterVal === 'string') {
        requestFieldsDict[filter] = filterVal;
      }
    });
  }

  const criteriaRanges = criteria.secondary?.ranges;
  if (criteriaRanges) {
    const prices = getValueFromRanges(SortAndFilterOptions.PRICE_BUCKETS, criteriaRanges);
    if (prices.length) {
      requestFieldsDict.price = prices;
    }
  }

  return requestFieldsDict;
};

export const adaptRequestMultiItemFieldsToDict = (
  multiItemId: string,
  bexApiResults: OperationResult<LodgingPwaPropertySearch.Query> = null
): any => {
  const requestFieldsDict: any = {};
  if (multiItemId) {
    requestFieldsDict.misId = multiItemId;
  }

  if (bexApiResults) {
    const primaryFlight = bexApiResults.data.propertySearch.multiItemSearchContext?.flights?.[0]?.primary;
    if (primaryFlight) {
      requestFieldsDict.origin = primaryFlight.journeyCriterias[0].origin;
      requestFieldsDict.cabinClass = primaryFlight.searchPreferences?.cabinClass;
      requestFieldsDict.tripType = primaryFlight.tripType;
      requestFieldsDict.airlineCode = primaryFlight.searchPreferences?.airline;
      requestFieldsDict.directFlights = String(
        primaryFlight.searchPreferences?.advancedFilters?.includes(FlightsAdvancedFilterType.NONSTOP)
      );

      requestFieldsDict.infantsInSeats =
        primaryFlight.travelers.filter(traveler => traveler.type === TravelerDetailsType.INFANT_IN_SEAT).length > 0 ? '1' : '0';

      if (primaryFlight.journeyCriterias.length) {
        const dateRange = bexApiResults.data.propertySearch.criteria.primary.dateRange;
        if (dateRange) {
          const { checkInDate, checkOutDate } = dateRange;
          const startDate = getDateStringFromDateRange(checkInDate);
          const endDate = getDateStringFromDateRange(checkOutDate);

          const isOneWay = primaryFlight.tripType === FlightsTripType.ONE_WAY;
          const tripStartDate = getDateStringFromDateRange(primaryFlight.journeyCriterias[0].departureDate);
          const tripEndDate = isOneWay ? endDate : getDateStringFromDateRange(primaryFlight.journeyCriterias[1].departureDate);
          if (startDate !== tripStartDate || tripEndDate !== endDate) {
            requestFieldsDict.partialStay = 'true';
            requestFieldsDict.partialStayStartDate = startDate;
            requestFieldsDict.partialStayEndDate = endDate;
            requestFieldsDict.startDate = tripStartDate;
            requestFieldsDict.endDate = tripEndDate;
          } else {
            requestFieldsDict.partialStay = 'false';
          }
        }
      }
    }

    const primaryCar = bexApiResults.data.propertySearch.multiItemSearchContext?.cars?.[0]?.primary;
    if (primaryCar) {
      requestFieldsDict.pickUpTime = getLocKeyFromBexApiDateTime(primaryCar.pickUpDateTime);
      requestFieldsDict.dropOffTime = getLocKeyFromBexApiDateTime(primaryCar.dropOffDateTime);
    }
  }

  return requestFieldsDict;
};

export const getValueFromSelections = (field: string, selections: LodgingPwaPropertySearch.Selections[]): string | null =>
  selections.find(selection => selection.id === field)?.value;

export const getListFromSelections = (field: string, selections: LodgingPwaPropertySearch.Selections[]): string[] =>
  selections.filter(selection => selection.id === field).map(selection => selection.value);

export const getValueFromRanges = (field: string, ranges: LodgingPwaPropertySearch.Ranges[]): string[] => {
  const selectedRange = ranges.find(range => range.id === field);

  return selectedRange ? [selectedRange.min.toString(), selectedRange.max.toString()] : [];
};

export const getValueFromCounts = (field: string, counts: LodgingPwaPropertySearch.Counts[]): number | null =>
  counts.find(count => count.id === field)?.value;
